import type { ReactNode } from 'react'
import { Loader2, Users } from 'lucide-react'
import { formatDate } from '@lib/helpers/format'
import type { ImpactedPlan, PlanImpactDto } from '@lib/dto/plan-impact.dto'
import { Button } from '@/components/ui/button'
import { FormError } from '@/components/ui/form-error'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

/** How many plans a dialog lists before it summarizes the rest. */
const LISTED = 8

interface PlanImpactDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  /** The sentence above the list of plans that will change, given how many there are. */
  intro: (count: number) => string
  /** What each plan's affected pieces are, e.g. "3 pieces outside the new walls". */
  piecesLabel?: ((pieces: number) => string) | undefined
  /** What is said about settled plans that keep things as they were; omitted when none. */
  settledNote?: ((count: number) => string) | undefined
  /** Heading when settled plans stop the change altogether. */
  blockedIntro?: string | undefined
  impact: PlanImpactDto | null
  loading: boolean
  error: string | null
  confirmLabel: string
  destructive?: boolean | undefined
  busy?: boolean | undefined
  onConfirm: () => void
  /**
   * Ask again after the check failed. Given, a failed check blocks the change:
   * this is offered in place of confirming, because a description that could
   * not be read might be leaving out the very plans the change reaches.
   */
  onRetry?: (() => void) | undefined
  /** Choices for the change itself, shown above the buttons. */
  children?: ReactNode
}

/** "May 14, 2027 · Ceremony", or just the plan when the event is undated. */
function planWhen(plan: ImpactedPlan): string {
  return plan.eventDate ? `${formatDate(plan.eventDate)} · ${plan.phaseLabel}` : plan.phaseLabel
}

/** "Priya is editing now" / "Priya and Sam are editing now". */
function editingLine(names: readonly string[]): string {
  if (names.length === 1) return `${names[0] ?? ''} is editing now`
  return `${names.slice(0, -1).join(', ')} and ${names.at(-1) ?? ''} are editing now`
}

/** The plans, one per row, first few named and the rest counted. */
export function PlanList({
  plans,
  piecesLabel,
}: {
  plans: readonly ImpactedPlan[]
  piecesLabel?: ((pieces: number) => string) | undefined
}) {
  const shown = plans.slice(0, LISTED)
  const more = plans.length - shown.length
  return (
    <ul className="max-h-64 space-y-1.5 overflow-y-auto rounded-md border border-line p-2 text-sm">
      {shown.map((plan) => (
        <li key={plan.phaseId} className="flex flex-col gap-0.5">
          <span className="font-medium text-ink">{plan.eventName}</span>
          <span className="text-xs text-ink-muted">
            {planWhen(plan)}
            {piecesLabel && plan.pieces > 0 ? ` — ${piecesLabel(plan.pieces)}` : ''}
          </span>
          {plan.editingNow.length > 0 ? (
            <span className="inline-flex items-center gap-1 text-xs text-hold-ink">
              <Users className="h-3 w-3" /> {editingLine(plan.editingNow)}
            </span>
          ) : null}
        </li>
      ))}
      {more > 0 ? <li className="text-xs text-ink-muted">and {more} more</li> : null}
    </ul>
  )
}

/**
 * A confirmation that says what a change will do to the event plans that
 * depend on it, before it is made: which events, which plans, how many pieces,
 * and who has them open right now.
 *
 * "Are you sure?" asks somebody to guess what they are about to break; this
 * tells them. When settled plans stop the change, it says so and offers no way
 * to go ahead.
 */
export function PlanImpactDialog(props: PlanImpactDialogProps) {
  const blocked = (props.impact?.blocking.length ?? 0) > 0
  const plans = props.impact?.plans ?? []
  const unchecked = props.onRetry !== undefined && !props.loading && props.impact === null && props.error !== null
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{props.title}</DialogTitle>
          <DialogDescription>
            {props.loading ? 'Checking which plans this reaches…' : null}
            {!props.loading && props.impact && blocked ? props.blockedIntro : null}
            {!props.loading && props.impact && !blocked ? props.intro(plans.length) : null}
          </DialogDescription>
        </DialogHeader>
        {props.loading ? (
          <div className="flex items-center gap-2 text-sm text-ink-muted">
            <Loader2 className="h-4 w-4 animate-spin" /> Checking…
          </div>
        ) : null}
        {props.error ? <FormError>{props.error}</FormError> : null}
        {props.impact && blocked ? <PlanList plans={props.impact.blocking} /> : null}
        {props.impact && !blocked && plans.length > 0 ? (
          <PlanList plans={plans} piecesLabel={props.piecesLabel} />
        ) : null}
        {props.impact && !blocked && props.settledNote && props.impact.settledKept > 0 ? (
          <p className="text-xs text-ink-muted">{props.settledNote(props.impact.settledKept)}</p>
        ) : null}
        {!blocked ? props.children : null}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => props.onOpenChange(false)}>
            {blocked ? 'Close' : 'Cancel'}
          </Button>
          {unchecked ? (
            <Button type="button" onClick={props.onRetry}>
              Check again
            </Button>
          ) : null}
          {!blocked && !unchecked ? (
            <Button
              type="button"
              variant={props.destructive ? 'destructive' : 'default'}
              disabled={props.loading || props.busy || (props.impact === null && props.error === null)}
              onClick={props.onConfirm}
              pending={props.busy}
            >
              {props.confirmLabel}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
